import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaArrowLeft as FaArrowLeftRaw,
  FaCheck as FaCheckRaw,
  FaCog as FaCogRaw,
  FaEye as FaEyeRaw,
  FaFileAlt as FaFileAltRaw,
  FaFilm as FaFilmRaw,
  FaImage as FaImageRaw,
  FaLightbulb as FaLightbulbRaw,
  FaLink as FaLinkRaw,
  FaParagraph as FaParagraphRaw,
  FaQuestion as FaQuestionRaw,
  FaSave as FaSaveRaw,
  FaTrashAlt as FaTrashAltRaw,
  FaClipboardList as FaClipboardListRaw,
} from 'react-icons/fa';
import { teacherService } from '../../services/classroom/teacherService';
import { supabase } from '../../services/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaArrowLeft: any = FaArrowLeftRaw;
const FaCheck: any = FaCheckRaw;
const FaCog: any = FaCogRaw;
const FaEye: any = FaEyeRaw;
const FaFileAlt: any = FaFileAltRaw;
const FaFilm: any = FaFilmRaw;
const FaImage: any = FaImageRaw;
const FaLightbulb: any = FaLightbulbRaw;
const FaLink: any = FaLinkRaw;
const FaParagraph: any = FaParagraphRaw;
const FaQuestion: any = FaQuestionRaw;
const FaSave: any = FaSaveRaw;
const FaTrashAlt: any = FaTrashAltRaw;
const FaClipboardList: any = FaClipboardListRaw;

const BUILDER_BLUE = '#2c7be5';
const BUILDER_BLUE_SOFT = 'color-mix(in srgb, var(--primary) 12%, var(--surface))';
const TEXT_BLOCK_ACCENT = '#ea580c';

const MOBILE = '1024px';

const Shell = styled.div`
  --builder-accent: ${BUILDER_BLUE};
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
  margin: -0.5rem -0.25rem 0;
  @media (max-width: ${MOBILE}) {
    margin: 0;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 260px;
  gap: 1.35rem;
  align-items: start;
  @media (max-width: ${MOBILE}) {
    grid-template-columns: 1fr;
  }
`;

const SideLabel = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const StepperCard = styled.aside`
  border: 1px solid var(--outline);
  border-radius: 14px;
  background: color-mix(in srgb, var(--surface) 92%, var(--surfaceVariant));
  padding: 1rem 0.75rem;
  position: sticky;
  top: 0.75rem;
  @media (max-width: ${MOBILE}) {
    position: static;
  }
`;

const StepRow = styled.button<{ $active?: boolean; $done?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  width: 100%;
  padding: 0.55rem 0.5rem;
  margin-bottom: 0.15rem;
  border: none;
  border-radius: 10px;
  background: ${({ $active }) => ($active ? BUILDER_BLUE_SOFT : 'transparent')};
  cursor: pointer;
  text-align: left;
  font: inherit;
  color: ${({ $active, $done }) =>
    $active ? 'var(--primary)' : $done ? 'var(--success, #1a8f5c)' : 'var(--textSecondary, var(--onSurfaceVariant))'};
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const StepIconWrap = styled.span<{ $active?: boolean }>`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  background: ${({ $active }) => ($active ? 'var(--primary)' : 'var(--surfaceVariant)')};
  color: ${({ $active }) => ($active ? 'var(--onPrimary)' : 'var(--textSecondary, var(--onSurfaceVariant))')};
`;

const StepText = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
`;

const CurrentCourseCard = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 0.65rem;
  border-radius: 12px;
  background: ${BUILDER_BLUE_SOFT};
  border: 1px solid color-mix(in srgb, var(--primary) 22%, var(--outline));
`;

const CurrentCourseLabel = styled.div`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-bottom: 0.25rem;
`;

const CurrentCourseName = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--primary);
  line-height: 1.3;
`;

/** Middle grid column: main content + inline footer (not viewport-fixed). */
const MiddleColumnWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

const MainColumn = styled.main`
  min-width: 0;
`;

const MainTitle = styled.h1`
  margin: 0 0 0.35rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 2.5vw, 1.75rem);
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
`;

const MainSubtitle = styled.p`
  margin: 0 0 1.25rem;
  font-size: 0.92rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
  max-width: 40rem;
`;

const BlockCard = styled.article<{ $accent: 'blue' | 'orange' | 'purple' | 'gray' }>`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  margin-bottom: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border-left: 4px solid
    ${({ $accent }) =>
      $accent === 'blue'
        ? BUILDER_BLUE
        : $accent === 'orange'
          ? TEXT_BLOCK_ACCENT
          : $accent === 'purple'
            ? '#7c3aed'
            : 'var(--outline)'};
`;

const BlockHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem 0.5rem;
`;

const BlockHeadLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  min-width: 0;
`;

const BlockIconBox = styled.span<{ $t: 'blue' | 'orange' | 'purple' | 'gray' }>`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $t }) =>
    $t === 'blue' ? BUILDER_BLUE : $t === 'orange' ? TEXT_BLOCK_ACCENT : $t === 'purple' ? '#7c3aed' : 'var(--textSecondary)'};
  background: ${({ $t }) =>
    $t === 'blue'
      ? 'color-mix(in srgb, #2c7be5 14%, transparent)'
      : $t === 'orange'
        ? 'color-mix(in srgb, #ea580c 14%, transparent)'
        : $t === 'purple'
          ? 'color-mix(in srgb, #7c3aed 14%, transparent)'
          : 'var(--surfaceVariant)'};
`;

const BlockTitle = styled.input`
  border: none;
  background: transparent;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
  width: 100%;
  min-width: 0;
  padding: 0;
  outline: none;
`;

const BlockMeta = styled.div`
  font-size: 0.72rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-top: 0.15rem;
`;

const IconButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--textSecondary, var(--onSurfaceVariant));
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
    color: var(--error, #dc2626);
  }
`;

const BlockBody = styled.div`
  padding: 0 1rem 1rem;
`;

const VideoPlaceholder = styled.div`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: linear-gradient(145deg, #1e3a5f 0%, #f97316 45%, #fbbf24 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.92);
  color: ${BUILDER_BLUE};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
`;

const BlockTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--text);
  background: var(--surface);
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;
`;

const BlockFieldGrid = styled.div`
  display: grid;
  gap: 0.65rem;
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.76rem;
    font-weight: 700;
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
  input,
  select,
  textarea {
    width: 100%;
    padding: 0.55rem 0.65rem;
    border-radius: 8px;
    border: 1px solid var(--outline);
    font-size: 0.86rem;
    font-family: inherit;
    color: var(--text);
    background: var(--surface);
    box-sizing: border-box;
  }
`;

const BlockRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`;

const TinyButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.42rem 0.65rem;
  cursor: pointer;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BlockHelp = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const DropZone = styled.div`
  border: 2px dashed var(--outline);
  border-radius: 12px;
  padding: 1.75rem 1rem;
  text-align: center;
  font-size: 0.88rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  background: color-mix(in srgb, var(--surfaceVariant) 55%, var(--surface));
  margin-top: 0.25rem;
`;

const RightColumn = styled.aside`
  position: sticky;
  top: 0.75rem;
  @media (max-width: ${MOBILE}) {
    position: static;
  }
`;

const LibraryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
`;

const LibraryBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.75rem 0.4rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--text);
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  min-height: 76px;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: ${BUILDER_BLUE_SOFT};
  }
`;

const StatusRow = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--outline);
`;

const AutoSaveBadge = styled.span`
  display: inline-block;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: var(--success, #1a8f5c);
  margin-bottom: 0.25rem;
`;

const LastSaved = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const ProTip = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 0.75rem;
  border-radius: 12px;
  background: ${BUILDER_BLUE_SOFT};
  border: 1px solid color-mix(in srgb, var(--primary) 18%, var(--outline));
`;

const ProTipHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: var(--primary);
  margin-bottom: 0.4rem;
`;

const ProTipBody = styled.p`
  margin: 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const BuilderFooterBar = styled.footer`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding: 0.85rem 1rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
`;

const FooterLeft = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
`;

const FooterLinkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.65rem;
  border: none;
  background: transparent;
  font-size: 0.84rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  cursor: pointer;
  font-family: inherit;
  &:hover {
    color: var(--primary);
  }
`;

const FooterPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1.15rem;
  border: none;
  border-radius: 10px;
  background: var(--primary);
  color: var(--onPrimary);
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    background: var(--primaryDark);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const FooterSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.6rem 1rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--text);
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const FooterActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoPanel = styled.section`
  border: 1px solid var(--outline);
  border-radius: 14px;
  background: var(--surface);
  padding: 1rem 1.1rem;
  margin-bottom: 1rem;
`;

const FieldGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  label {
    display: flex;
    flex-direction: column;
    gap: 0.28rem;
    font-size: 0.78rem;
    font-weight: 700;
    color: var(--textSecondary, var(--onSurfaceVariant));
  }
  input,
  textarea {
    padding: 0.55rem 0.7rem;
    border-radius: 8px;
    border: 1px solid var(--outline);
    font-size: 0.88rem;
    font-family: inherit;
    color: var(--text);
    background: var(--surface);
  }
`;

const ErrorText = styled.p`
  margin: 0.35rem 0 0;
  color: var(--error, #dc2626);
  font-size: 0.8rem;
  font-weight: 600;
`;

const Hint = styled.p`
  margin: 0;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

type BuilderStep = 1 | 2 | 3 | 4;

type ContentBlockType = 'text' | 'video' | 'image' | 'file' | 'pdf_pages' | 'quiz' | 'assignment' | 'links';

type ContentBlock = {
  id: string;
  type: ContentBlockType;
  title: string;
  meta: string;
  body: string;
  url?: string;
  assetUrl?: string;
  assetName?: string;
  quizId?: string;
  quizTitle?: string;
  assignmentId?: string;
  assignmentTitle?: string;
  links?: string[];
  textbookId?: string;
  textbookTitle?: string;
  pdfUrl?: string;
  pdfPageExpression?: string;
  pdfPages?: number[];
  textbookPageCount?: number;
};

const LESSON_CONTENT_VERSION = 1;
const LESSON_ASSET_BUCKET = 'lesson-assets';
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function normalizeBlock(raw: unknown): ContentBlock | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.id !== 'string') return null;
  const rawType = typeof row.type === 'string' ? row.type : 'text';
  const type: ContentBlockType =
    rawType === 'interactive'
      ? 'links'
      : rawType === 'links' ||
          rawType === 'video' ||
          rawType === 'image' ||
          rawType === 'file' ||
          rawType === 'pdf_pages' ||
          rawType === 'quiz' ||
          rawType === 'assignment'
        ? rawType
        : 'text';
  const body = typeof row.body === 'string' ? row.body : '';
  const links =
    Array.isArray(row.links) && row.links.every((v) => typeof v === 'string')
      ? (row.links as string[])
      : type === 'links' && body
        ? body
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean)
        : [];
  return {
    id: row.id,
    type,
    title: typeof row.title === 'string' ? row.title : 'Untitled block',
    meta: typeof row.meta === 'string' ? row.meta : '',
    body,
    url: typeof row.url === 'string' ? row.url : undefined,
    assetUrl: typeof row.assetUrl === 'string' ? row.assetUrl : undefined,
    assetName: typeof row.assetName === 'string' ? row.assetName : undefined,
    quizId: typeof row.quizId === 'string' ? row.quizId : undefined,
    quizTitle: typeof row.quizTitle === 'string' ? row.quizTitle : undefined,
    assignmentId: typeof row.assignmentId === 'string' ? row.assignmentId : undefined,
    assignmentTitle: typeof row.assignmentTitle === 'string' ? row.assignmentTitle : undefined,
    links,
    textbookId: typeof row.textbookId === 'string' ? row.textbookId : undefined,
    textbookTitle: typeof row.textbookTitle === 'string' ? row.textbookTitle : undefined,
    pdfUrl:
      typeof row.pdfUrl === 'string'
        ? row.pdfUrl
        : typeof row.assetUrl === 'string' && type === 'pdf_pages'
          ? row.assetUrl
          : undefined,
    pdfPageExpression: typeof row.pdfPageExpression === 'string' ? row.pdfPageExpression : undefined,
    pdfPages:
      Array.isArray(row.pdfPages) && row.pdfPages.every((value) => typeof value === 'number' && Number.isFinite(value))
        ? (row.pdfPages as number[]).map((value) => Math.max(1, Math.floor(value)))
        : undefined,
    textbookPageCount:
      typeof row.textbookPageCount === 'number' && Number.isFinite(row.textbookPageCount)
        ? Math.max(1, Math.floor(row.textbookPageCount))
        : undefined,
  };
}

function parseLinksFromText(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function isValidYoutubeUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  try {
    const url = new URL(trimmed);
    const host = url.hostname.replace(/^www\./i, '').toLowerCase();
    return host === 'youtube.com' || host === 'youtu.be' || host === 'm.youtube.com';
  } catch {
    return false;
  }
}

function parsePdfPageExpression(expression: string, maxPage: number): { pages: number[]; error?: string } {
  const trimmed = expression.trim();
  if (!trimmed) return { pages: [], error: 'Enter at least one page number.' };
  if (!Number.isFinite(maxPage) || maxPage <= 0) return { pages: [], error: 'Select a textbook first.' };
  const out = new Set<number>();
  const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length === 0) return { pages: [], error: 'Enter at least one page number.' };
  for (const part of parts) {
    const range = part.match(/^(\d+)\s*-\s*(\d+)$/);
    if (range) {
      const start = Number(range[1]);
      const end = Number(range[2]);
      if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end <= 0) {
        return { pages: [], error: `Invalid range "${part}".` };
      }
      if (start > end) return { pages: [], error: `Range start must be before end in "${part}".` };
      if (end > maxPage) return { pages: [], error: `Page range "${part}" exceeds textbook page count (${maxPage}).` };
      for (let page = start; page <= end; page += 1) out.add(page);
      continue;
    }

    const single = Number(part);
    if (!Number.isFinite(single) || single <= 0 || !/^\d+$/.test(part)) {
      return { pages: [], error: `Invalid page "${part}".` };
    }
    if (single > maxPage) return { pages: [], error: `Page "${part}" exceeds textbook page count (${maxPage}).` };
    out.add(single);
  }

  const pages = Array.from(out).sort((a, b) => a - b);
  if (pages.length === 0) return { pages: [], error: 'Enter at least one page number.' };
  return { pages };
}

function parseStoredDescription(raw: string | null | undefined): { plainDescription: string; blocks: ContentBlock[] } {
  if (!raw?.trim()) return { plainDescription: '', blocks: [] };
  try {
    const parsed = JSON.parse(raw) as { version?: number; blocks?: unknown[]; plainDescription?: string };
    if (parsed && typeof parsed === 'object' && parsed.version === LESSON_CONTENT_VERSION && Array.isArray(parsed.blocks)) {
      return {
        plainDescription: typeof parsed.plainDescription === 'string' ? parsed.plainDescription : '',
        blocks: parsed.blocks.map((row) => normalizeBlock(row)).filter((row): row is ContentBlock => Boolean(row)),
      };
    }
  } catch {
    /* not JSON */
  }
  return { plainDescription: raw, blocks: [] };
}

function serializeLessonDescription(plainDescription: string, blocks: ContentBlock[]): string {
  const payload = {
    version: LESSON_CONTENT_VERSION,
    plainDescription: plainDescription.trim(),
    blocks,
  };
  return JSON.stringify(payload);
}

function newId(): string {
  return `blk_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function defaultBlock(type: ContentBlockType): ContentBlock {
  switch (type) {
    case 'video':
      return {
        id: newId(),
        type: 'video',
        title: 'Lecture: New video',
        meta: 'YouTube URL',
        body: '',
        url: '',
      };
    case 'text':
      return {
        id: newId(),
        type: 'text',
        title: 'Core concepts',
        meta: 'Formatted Text Block',
        body:
          'Use this space for readings, key definitions, or discussion prompts for your students.',
      };
    case 'image':
      return { id: newId(), type: 'image', title: 'Image', meta: 'Upload image and optional caption', body: '', assetUrl: '', assetName: '' };
    case 'file':
      return { id: newId(), type: 'file', title: 'Downloadable file', meta: 'Upload file for students', body: '', assetUrl: '', assetName: '' };
    case 'pdf_pages':
      return {
        id: newId(),
        type: 'pdf_pages',
        title: 'Textbook pages',
        meta: 'Select textbook pages for students',
        body: '',
        textbookId: '',
        textbookTitle: '',
        pdfUrl: '',
        pdfPageExpression: '',
        pdfPages: [],
      };
    case 'quiz':
      return { id: newId(), type: 'quiz', title: 'Quick check', meta: 'Pick an existing quiz or create one', body: '', quizId: '', quizTitle: '' };
    case 'assignment':
      return {
        id: newId(),
        type: 'assignment',
        title: 'Linked assignment',
        meta: 'Pick an existing assignment for this lesson',
        body: '',
        assignmentId: '',
        assignmentTitle: '',
      };
    case 'links':
      return {
        id: newId(),
        type: 'links',
        title: 'External links',
        meta: 'Paste one external link per line',
        body: '',
        links: [],
      };
    default:
      return { id: newId(), type: 'text', title: 'Text', meta: 'Block', body: '' };
  }
}

function blockAccent(type: ContentBlockType): 'blue' | 'orange' | 'purple' | 'gray' {
  if (type === 'video') return 'blue';
  if (type === 'text') return 'orange';
  if (type === 'pdf_pages') return 'blue';
  if (type === 'quiz' || type === 'assignment') return 'purple';
  return 'gray';
}

function blockIcon(type: ContentBlockType) {
  switch (type) {
    case 'video':
      return FaFilm;
    case 'text':
      return FaParagraph;
    case 'image':
      return FaImage;
    case 'file':
      return FaFileAlt;
    case 'pdf_pages':
      return FaFileAlt;
    case 'quiz':
      return FaQuestion;
    case 'assignment':
      return FaClipboardList;
    case 'links':
      return FaLink;
    default:
      return FaParagraph;
  }
}

function formatRelativeSaved(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'just now';
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 10) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  return new Date(iso).toLocaleString();
}

const ClassroomTeacherClassLessonEditorPage: React.FC = () => {
  const { classId = '', lessonId } = useParams<{ classId: string; lessonId?: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [overviewQuery, lessonsQuery, assignmentsQuery] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
      { queryKey: ['classroom', 'teacher', 'lessons'], queryFn: teacherService.getLessons },
      { queryKey: ['classroom', 'teacher', 'work', 'assignment'], queryFn: () => teacherService.getWork('assignment') },
    ],
  });

  const classItem = useMemo(
    () => (overviewQuery.data ?? []).find((row) => row.id === classId),
    [overviewQuery.data, classId]
  );
  const quizzesQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'work', 'quiz'],
    queryFn: () => teacherService.getWork('quiz'),
  });
  const textbooksQuery = useQuery({
    queryKey: ['classroom', 'teacher', 'textbooks', classId],
    queryFn: () => teacherService.getClassTextbooks(classId),
    enabled: Boolean(classId.trim()),
  });

  const existingLesson = useMemo(() => {
    if (!lessonId) return null;
    return (lessonsQuery.data ?? []).find((row) => row.id === lessonId && row.class_id === classId) ?? null;
  }, [lessonsQuery.data, lessonId, classId]);

  const [activeStep, setActiveStep] = useState<BuilderStep>(1);
  const [title, setTitle] = useState('');
  const [lessonDate, setLessonDate] = useState('');
  const [plainDescription, setPlainDescription] = useState('');
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [titleError, setTitleError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingImageFiles, setPendingImageFiles] = useState<Record<string, File | null>>({});
  const [pendingResourceFiles, setPendingResourceFiles] = useState<Record<string, File | null>>({});
  const [blockActionError, setBlockActionError] = useState<Record<string, string>>({});
  const [uploadingBlockId, setUploadingBlockId] = useState<string | null>(null);
  const [quizCreatorOpen, setQuizCreatorOpen] = useState<Record<string, boolean>>({});
  const [quizDrafts, setQuizDrafts] = useState<Record<string, { title: string; dueAt: string; points: number }>>({});
  const [creatingQuizBlockId, setCreatingQuizBlockId] = useState<string | null>(null);
  const classQuizzes = useMemo(
    () => (quizzesQuery.data ?? []).filter((row) => row.class_id === classId && !row.archived),
    [quizzesQuery.data, classId]
  );
  const classAssignments = useMemo(
    () => (assignmentsQuery.data ?? []).filter((row) => row.class_id === classId && !row.archived),
    [assignmentsQuery.data, classId]
  );
  const classTextbooks = useMemo(
    () => (textbooksQuery.data ?? []).filter((row) => row.class_id === classId),
    [textbooksQuery.data, classId]
  );
  const textbookById = useMemo(() => new Map(classTextbooks.map((book) => [book.id, book])), [classTextbooks]);

  useEffect(() => {
    if (existingLesson) {
      setTitle(existingLesson.title);
      setLessonDate((existingLesson.lesson_date ?? '').slice(0, 10));
      const parsed = parseStoredDescription(existingLesson.description);
      setPlainDescription(parsed.plainDescription);
      setBlocks(parsed.blocks.length ? parsed.blocks : []);
      return;
    }
    setTitle('');
    setLessonDate('');
    setPlainDescription('');
    setBlocks([]);
    setActiveStep(1);
  }, [existingLesson]);

  const markSavedNow = useCallback(() => {
    setLastSavedAt(new Date().toISOString());
  }, []);

  const saveLesson = useMutation({
    mutationFn: teacherService.saveLesson,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'lessons'] });
      markSavedNow();
    },
  });
  const saveQuiz = useMutation({
    mutationFn: teacherService.saveWork,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classroom', 'teacher', 'work', 'quiz'] });
    },
  });

  const persistLesson = useCallback(
    (opts?: { navigateAway?: boolean }) => {
      const lessonsInClass = (lessonsQuery.data ?? []).filter((row) => row.class_id === classId);
      const maxSequence = lessonsInClass.reduce((max, row) => Math.max(max, row.sequence), 0);
      const trimmedTitle = title.trim();
      if (!trimmedTitle) {
        setTitleError('Lesson title is required.');
        setActiveStep(1);
        return;
      }
      setTitleError('');
      const invalidPdfBlocks = new Set<string>();
      const normalizedBlocks = blocks.map((block) => {
        if (block.type !== 'pdf_pages') return block;
        const textbook = block.textbookId ? textbookById.get(block.textbookId) ?? null : null;
        if (!textbook) {
          invalidPdfBlocks.add(block.id);
          return block;
        }
        const parsed = parsePdfPageExpression(block.pdfPageExpression ?? '', textbook.page_count);
        if (parsed.error) invalidPdfBlocks.add(block.id);
        return {
          ...block,
          textbookId: textbook.id,
          textbookTitle: textbook.title,
          textbookPageCount: textbook.page_count,
          pdfUrl: textbook.file_url,
          pdfPages: parsed.pages,
        };
      });
      if (invalidPdfBlocks.size > 0) {
        setBlockActionError((prev) => {
          const next = { ...prev };
          invalidPdfBlocks.forEach((id) => {
            next[id] = next[id] || 'Fix textbook and page selections before saving this lesson.';
          });
          return next;
        });
        setActiveStep(2);
        return;
      }
      const description = serializeLessonDescription(plainDescription, normalizedBlocks);
      saveLesson.mutate(
        {
          id: existingLesson?.id,
          class_id: classId,
          title: trimmedTitle,
          lesson_date: lessonDate || null,
          description,
          sequence: existingLesson?.sequence ?? maxSequence + 1,
        },
        {
          onSuccess: () => {
            if (opts?.navigateAway) {
              navigate(`/classroom-teacher/classes/${encodeURIComponent(classId)}/lessons`);
            }
          },
        }
      );
    },
    [
      blocks,
      classId,
      existingLesson?.id,
      existingLesson?.sequence,
      lessonDate,
      lessonsQuery.data,
      navigate,
      plainDescription,
      saveLesson,
      textbookById,
      title,
    ]
  );

  const addBlock = (type: ContentBlockType) => {
    setBlocks((prev) => [...prev, defaultBlock(type)]);
    if (activeStep === 1) setActiveStep(2);
  };

  const removeBlock = (id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
    setPendingImageFiles((prev) => ({ ...prev, [id]: null }));
    setPendingResourceFiles((prev) => ({ ...prev, [id]: null }));
    setBlockActionError((prev) => ({ ...prev, [id]: '' }));
  };

  const updateBlock = (id: string, patch: Partial<ContentBlock>) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  };

  const updateLinksBody = (id: string, value: string) => {
    updateBlock(id, { body: value, links: parseLinksFromText(value) });
  };

  const updateBlockError = (id: string, message: string) => {
    setBlockActionError((prev) => ({ ...prev, [id]: message }));
  };

  const uploadBlockAsset = async (block: ContentBlock, mode: 'image' | 'file') => {
    const selectedFile = mode === 'image' ? pendingImageFiles[block.id] : pendingResourceFiles[block.id];
    if (!selectedFile) {
      updateBlockError(block.id, `Select a ${mode} file first.`);
      return;
    }

    if (mode === 'image') {
      if (!selectedFile.type.startsWith('image/')) {
        updateBlockError(block.id, 'Only image files are allowed for image blocks.');
        return;
      }
      if (selectedFile.size > MAX_IMAGE_SIZE_BYTES) {
        updateBlockError(block.id, 'Image must be 5MB or smaller.');
        return;
      }
    } else if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
      updateBlockError(block.id, 'File must be 20MB or smaller.');
      return;
    }

    updateBlockError(block.id, '');
    setUploadingBlockId(block.id);
    try {
      const safeName = sanitizeFileName(selectedFile.name);
      const path = `classroom/lessons/${classId}/${mode}/${Date.now()}_${Math.random().toString(36).slice(2)}_${safeName}`;
      const { error: uploadError } = await supabase.storage.from(LESSON_ASSET_BUCKET).upload(path, selectedFile, { upsert: false });
      if (uploadError) {
        updateBlockError(block.id, uploadError.message);
        return;
      }
      const { data } = supabase.storage.from(LESSON_ASSET_BUCKET).getPublicUrl(path);
      updateBlock(block.id, {
        assetUrl: data.publicUrl,
        assetName: selectedFile.name,
        meta: mode === 'image' ? `Image uploaded: ${selectedFile.name}` : `File uploaded: ${selectedFile.name}`,
      });
      if (mode === 'image') {
        setPendingImageFiles((prev) => ({ ...prev, [block.id]: null }));
      } else {
        setPendingResourceFiles((prev) => ({ ...prev, [block.id]: null }));
      }
    } finally {
      setUploadingBlockId(null);
    }
  };

  const createInlineQuiz = async (block: ContentBlock) => {
    const draft = quizDrafts[block.id] ?? { title: '', dueAt: '', points: 10 };
    if (!draft.title.trim()) {
      updateBlockError(block.id, 'Quiz title is required.');
      return;
    }
    updateBlockError(block.id, '');
    setCreatingQuizBlockId(block.id);
    saveQuiz.mutate(
      {
        class_id: classId,
        title: draft.title.trim(),
        due_at: draft.dueAt ? new Date(draft.dueAt).toISOString() : null,
        points: Number.isFinite(draft.points) && draft.points > 0 ? draft.points : 10,
        archived: false,
        type: 'quiz',
      },
      {
        onSuccess: (result) => {
          if (!result.ok || !result.data) {
            updateBlockError(block.id, result.error ?? 'Unable to create quiz.');
            return;
          }
          updateBlock(block.id, {
            quizId: result.data.id,
            quizTitle: result.data.title,
            meta: `Linked quiz: ${result.data.title}`,
          });
          setQuizCreatorOpen((prev) => ({ ...prev, [block.id]: false }));
          setQuizDrafts((prev) => ({ ...prev, [block.id]: { title: '', dueAt: '', points: 10 } }));
        },
        onError: (error) => {
          updateBlockError(block.id, (error as { message?: string }).message ?? 'Unable to create quiz.');
        },
        onSettled: () => {
          setCreatingQuizBlockId(null);
        },
      }
    );
  };

  const handleSaveDraft = () => persistLesson({ navigateAway: false });

  const handlePublishAndLeave = () => persistLesson({ navigateAway: true });

  const steps: { n: BuilderStep; label: string; icon: React.ReactNode }[] = [
    { n: 1, label: 'Lesson Info', icon: <FaFileAlt size={12} aria-hidden /> },
    { n: 2, label: 'Build Content', icon: <FaParagraph size={12} aria-hidden /> },
    { n: 3, label: 'Assignment', icon: <FaClipboardList size={12} aria-hidden /> },
    { n: 4, label: 'Settings', icon: <FaCog size={12} aria-hidden /> },
  ];

  if (overviewQuery.isLoading || lessonsQuery.isLoading || assignmentsQuery.isLoading || textbooksQuery.isLoading) {
    return <Hint>Loading lesson editor…</Hint>;
  }

  if (overviewQuery.isError || lessonsQuery.isError || assignmentsQuery.isError || textbooksQuery.isError) {
    return <Hint>Unable to load lesson editor.</Hint>;
  }

  if (!classItem) {
    return (
      <Shell>
        <BackLink to="/classroom-teacher/classes">
          <FaArrowLeft size={12} aria-hidden />
          Back to Classes
        </BackLink>
        <Hint>Class not found.</Hint>
      </Shell>
    );
  }

  if (lessonId && !existingLesson) {
    return (
      <Shell>
        <BackLink to={`/classroom-teacher/classes/${encodeURIComponent(classId)}/lessons`}>
          <FaArrowLeft size={12} aria-hidden />
          Back to Lessons
        </BackLink>
        <Hint>Lesson not found for this class.</Hint>
      </Shell>
    );
  }

  return (
    <Shell>
      <TopBar>
        <BackLink to={`/classroom-teacher/classes/${encodeURIComponent(classId)}/lessons`}>
          <FaArrowLeft size={12} aria-hidden />
          Back to Lessons
        </BackLink>
      </TopBar>

      <LayoutGrid>
        <div>
          <SideLabel>COURSE BUILDER</SideLabel>
          <StepperCard>
            {steps.map((s) => {
              const done = s.n < activeStep || (s.n === 1 && title.trim().length > 0);
              const active = s.n === activeStep;
              return (
                <StepRow
                  key={s.n}
                  type="button"
                  $active={active}
                  $done={done && !active}
                  onClick={() => setActiveStep(s.n)}
                >
                  <StepIconWrap $active={active}>
                    {done && !active ? <FaCheck size={12} aria-hidden /> : s.icon}
                  </StepIconWrap>
                  <StepText>{s.label}</StepText>
                </StepRow>
              );
            })}
            <CurrentCourseCard>
              <CurrentCourseLabel>CURRENT COURSE</CurrentCourseLabel>
              <CurrentCourseName>{classItem.name}</CurrentCourseName>
            </CurrentCourseCard>
          </StepperCard>
        </div>

        <MiddleColumnWrap>
        <MainColumn>
          {activeStep === 1 ? (
            <>
              <MainTitle>Lesson information</MainTitle>
              <MainSubtitle>Set the lesson title, schedule, and a short summary. You can add rich content in the next step.</MainSubtitle>
              <InfoPanel>
                <FieldGrid>
                  <label>
                    Lesson title
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g. Wave–particle duality"
                      aria-invalid={titleError ? 'true' : 'false'}
                    />
                  </label>
                  {titleError ? <ErrorText>{titleError}</ErrorText> : null}
                  <label>
                    Lesson date
                    <input type="date" value={lessonDate} onChange={(e) => setLessonDate(e.target.value)} />
                  </label>
                  <label>
                    Summary / notes
                    <textarea
                      rows={4}
                      value={plainDescription}
                      onChange={(e) => setPlainDescription(e.target.value)}
                      placeholder="Objectives, materials, or notes (optional)."
                    />
                  </label>
                </FieldGrid>
              </InfoPanel>
            </>
          ) : null}

          {activeStep === 2 ? (
            <>
              <MainTitle>Build Lesson Content</MainTitle>
              <MainSubtitle>Add and arrange your educational materials using the blocks on the right.</MainSubtitle>

              {blocks.map((block) => {
                const Icon = blockIcon(block.type);
                const accent = blockAccent(block.type);
                const quizDraft = quizDrafts[block.id] ?? { title: '', dueAt: '', points: 10 };
                const selectedQuizId = block.quizId ?? '';
                const selectedTextbook = classTextbooks.find((book) => book.id === (block.textbookId ?? '')) ?? null;
                const blockError = blockActionError[block.id];
                const youtubeInvalid = block.type === 'video' && !isValidYoutubeUrl(block.url ?? '');
                return (
                  <BlockCard key={block.id} $accent={accent}>
                    <BlockHead>
                      <BlockHeadLeft>
                        <BlockIconBox $t={accent} aria-hidden>
                          <Icon size={16} />
                        </BlockIconBox>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <BlockTitle
                            value={block.title}
                            onChange={(e) => updateBlock(block.id, { title: e.target.value })}
                            aria-label="Block title"
                          />
                          <BlockMeta>{block.meta}</BlockMeta>
                        </div>
                      </BlockHeadLeft>
                      <IconButton type="button" title="Remove block" onClick={() => removeBlock(block.id)} aria-label="Remove block">
                        <FaTrashAlt size={14} />
                      </IconButton>
                    </BlockHead>
                    <BlockBody>
                      {block.type === 'video' ? (
                        <BlockFieldGrid>
                          <VideoPlaceholder>
                            <PlayCircle aria-hidden>▶</PlayCircle>
                          </VideoPlaceholder>
                          <label>
                            YouTube URL
                            <input
                              type="url"
                              value={block.url ?? ''}
                              onChange={(e) => updateBlock(block.id, { url: e.target.value })}
                              placeholder="https://www.youtube.com/watch?v=..."
                            />
                          </label>
                          {youtubeInvalid ? <ErrorText>Please enter a valid YouTube URL.</ErrorText> : null}
                        </BlockFieldGrid>
                      ) : null}

                      {block.type === 'image' ? (
                        <BlockFieldGrid>
                          <label>
                            Image caption (optional)
                            <BlockTextarea
                              value={block.body}
                              onChange={(e) => updateBlock(block.id, { body: e.target.value })}
                              placeholder="Add a short caption or context for this image."
                              rows={3}
                            />
                          </label>
                          <label>
                            Select image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0] ?? null;
                                setPendingImageFiles((prev) => ({ ...prev, [block.id]: file }));
                                updateBlockError(block.id, '');
                              }}
                            />
                          </label>
                          <BlockRow>
                            <TinyButton
                              type="button"
                              onClick={() => {
                                void uploadBlockAsset(block, 'image');
                              }}
                              disabled={uploadingBlockId === block.id}
                            >
                              {uploadingBlockId === block.id ? 'Uploading image...' : 'Upload image'}
                            </TinyButton>
                            {block.assetName ? <BlockHelp>Uploaded: {block.assetName}</BlockHelp> : null}
                          </BlockRow>
                          {block.assetUrl ? (
                            <BlockHelp>
                              Image URL: <a href={block.assetUrl}>{block.assetUrl}</a>
                            </BlockHelp>
                          ) : null}
                        </BlockFieldGrid>
                      ) : null}

                      {block.type === 'file' ? (
                        <BlockFieldGrid>
                          <label>
                            Notes (optional)
                            <BlockTextarea
                              value={block.body}
                              onChange={(e) => updateBlock(block.id, { body: e.target.value })}
                              placeholder="Add instructions for this file."
                              rows={3}
                            />
                          </label>
                          <label>
                            Select file
                            <input
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files?.[0] ?? null;
                                setPendingResourceFiles((prev) => ({ ...prev, [block.id]: file }));
                                updateBlockError(block.id, '');
                              }}
                            />
                          </label>
                          <BlockRow>
                            <TinyButton
                              type="button"
                              onClick={() => {
                                void uploadBlockAsset(block, 'file');
                              }}
                              disabled={uploadingBlockId === block.id}
                            >
                              {uploadingBlockId === block.id ? 'Uploading file...' : 'Upload file'}
                            </TinyButton>
                            {block.assetName ? <BlockHelp>Uploaded: {block.assetName}</BlockHelp> : null}
                          </BlockRow>
                          {block.assetUrl ? (
                            <BlockHelp>
                              File URL: <a href={block.assetUrl}>{block.assetUrl}</a>
                            </BlockHelp>
                          ) : null}
                        </BlockFieldGrid>
                      ) : null}

                      {block.type === 'pdf_pages' ? (
                        <BlockFieldGrid>
                          <label>
                            Textbook PDF
                            <select
                              value={block.textbookId ?? ''}
                              onChange={(e) => {
                                const nextTextbookId = e.target.value;
                                const nextTextbook = classTextbooks.find((row) => row.id === nextTextbookId);
                                const existingExpression = block.pdfPageExpression ?? '';
                                const parsed = nextTextbook
                                  ? parsePdfPageExpression(existingExpression, nextTextbook.page_count)
                                  : { pages: [] };
                                updateBlock(block.id, {
                                  textbookId: nextTextbookId || '',
                                  textbookTitle: nextTextbook?.title ?? '',
                                  pdfUrl: nextTextbook?.file_url ?? '',
                                  textbookPageCount: nextTextbook?.page_count ?? undefined,
                                  meta: nextTextbook
                                    ? `Textbook: ${nextTextbook.title} (${nextTextbook.page_count} pages)`
                                    : 'Select textbook pages for students',
                                  pdfPages: parsed.pages,
                                });
                                updateBlockError(block.id, parsed.error ?? '');
                              }}
                            >
                              <option value="">Select a textbook...</option>
                              {classTextbooks.map((book) => (
                                <option key={book.id} value={book.id}>
                                  {book.title} ({book.page_count} pages)
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Page numbers
                            <input
                              value={block.pdfPageExpression ?? ''}
                              onChange={(e) => {
                                const expression = e.target.value;
                                updateBlock(block.id, { pdfPageExpression: expression });
                                if (!selectedTextbook) {
                                  updateBlockError(block.id, 'Select a textbook first.');
                                  return;
                                }
                                const parsed = parsePdfPageExpression(expression, selectedTextbook.page_count);
                                updateBlock(block.id, { pdfPages: parsed.pages, textbookPageCount: selectedTextbook.page_count });
                                updateBlockError(block.id, parsed.error ?? '');
                              }}
                              placeholder="Examples: 4, 8-10, 12"
                            />
                          </label>
                          <BlockHelp>
                            Use commas and ranges to pick pages. Example: <code>2,5-7,11</code>
                          </BlockHelp>
                          {selectedTextbook ? (
                            <BlockHelp>
                              {selectedTextbook.page_count} total pages available in this textbook.
                            </BlockHelp>
                          ) : (
                            <BlockHelp>
                              Add PDFs in <Link to={`/classroom-teacher/classes/${encodeURIComponent(classId)}/textbook`}>Textbook</Link>.
                            </BlockHelp>
                          )}
                          {block.pdfPages && block.pdfPages.length > 0 ? (
                            <BlockHelp>
                              Selected pages: {block.pdfPages.join(', ')}
                            </BlockHelp>
                          ) : null}
                          {selectedTextbook?.file_url ? (
                            <BlockHelp>
                              Source PDF: <a href={selectedTextbook.file_url}>Open textbook PDF</a>
                            </BlockHelp>
                          ) : null}
                          {textbooksQuery.isLoading ? <BlockHelp>Loading textbooks...</BlockHelp> : null}
                        </BlockFieldGrid>
                      ) : null}

                      {block.type === 'quiz' ? (
                        <BlockFieldGrid>
                          <label>
                            Select quiz
                            <select
                              value={selectedQuizId}
                              onChange={(e) => {
                                const nextQuizId = e.target.value;
                                const selectedQuiz = classQuizzes.find((q) => q.id === nextQuizId);
                                updateBlock(block.id, {
                                  quizId: nextQuizId || '',
                                  quizTitle: selectedQuiz?.title ?? '',
                                  meta: selectedQuiz ? `Linked quiz: ${selectedQuiz.title}` : 'Pick an existing quiz or create one',
                                });
                              }}
                            >
                              <option value="">Select a quiz...</option>
                              {classQuizzes.map((quiz) => (
                                <option key={quiz.id} value={quiz.id}>
                                  {quiz.title}
                                </option>
                              ))}
                            </select>
                          </label>
                          <BlockRow>
                            <TinyButton
                              type="button"
                              onClick={() =>
                                setQuizCreatorOpen((prev) => {
                                  const next = !prev[block.id];
                                  if (next && !quizDrafts[block.id]) {
                                    setQuizDrafts((draftPrev) => ({ ...draftPrev, [block.id]: { title: '', dueAt: '', points: 10 } }));
                                  }
                                  return { ...prev, [block.id]: next };
                                })
                              }
                            >
                              {quizCreatorOpen[block.id] ? 'Hide new quiz form' : 'Create new quiz'}
                            </TinyButton>
                            {quizzesQuery.isLoading ? <BlockHelp>Loading quizzes...</BlockHelp> : null}
                          </BlockRow>
                          {quizCreatorOpen[block.id] ? (
                            <BlockFieldGrid>
                              <label>
                                Quiz title
                                <input
                                  value={quizDraft.title}
                                  onChange={(e) =>
                                    setQuizDrafts((prev) => ({
                                      ...prev,
                                      [block.id]: { ...quizDraft, title: e.target.value },
                                    }))
                                  }
                                  placeholder="Quick quiz title"
                                />
                              </label>
                              <label>
                                Due date (optional)
                                <input
                                  type="datetime-local"
                                  value={quizDraft.dueAt}
                                  onChange={(e) =>
                                    setQuizDrafts((prev) => ({
                                      ...prev,
                                      [block.id]: { ...quizDraft, dueAt: e.target.value },
                                    }))
                                  }
                                />
                              </label>
                              <label>
                                Points
                                <input
                                  type="number"
                                  min={1}
                                  value={quizDraft.points}
                                  onChange={(e) =>
                                    setQuizDrafts((prev) => ({
                                      ...prev,
                                      [block.id]: { ...quizDraft, points: Number(e.target.value) || 10 },
                                    }))
                                  }
                                />
                              </label>
                              <TinyButton
                                type="button"
                                onClick={() => {
                                  void createInlineQuiz(block);
                                }}
                                disabled={creatingQuizBlockId === block.id}
                              >
                                {creatingQuizBlockId === block.id ? 'Creating quiz...' : 'Create and select quiz'}
                              </TinyButton>
                            </BlockFieldGrid>
                          ) : null}
                        </BlockFieldGrid>
                      ) : null}

                      {block.type === 'assignment' ? (
                        <BlockFieldGrid>
                          <label>
                            Select assignment
                            <select
                              value={block.assignmentId ?? ''}
                              onChange={(e) => {
                                const nextAssignmentId = e.target.value;
                                const selectedAssignment = classAssignments.find((a) => a.id === nextAssignmentId);
                                updateBlock(block.id, {
                                  assignmentId: nextAssignmentId || '',
                                  assignmentTitle: selectedAssignment?.title ?? '',
                                  title: selectedAssignment?.title || block.title,
                                  meta: selectedAssignment
                                    ? `Linked assignment: ${selectedAssignment.title}`
                                    : 'Pick an existing assignment for this lesson',
                                });
                              }}
                            >
                              <option value="">Select an assignment...</option>
                              {classAssignments.map((assignment) => (
                                <option key={assignment.id} value={assignment.id}>
                                  {assignment.title}
                                </option>
                              ))}
                            </select>
                          </label>
                          {block.assignmentId ? (
                            <BlockHelp>
                              Linked: {block.assignmentTitle || 'Assignment'}
                            </BlockHelp>
                          ) : (
                            <BlockHelp>
                              Need one first? Create it in <Link to="/classroom-teacher/assignments">Assignments</Link>.
                            </BlockHelp>
                          )}
                        </BlockFieldGrid>
                      ) : null}

                      {block.type === 'links' ? (
                        <BlockFieldGrid>
                          <label>
                            External links (one URL per line)
                            <BlockTextarea
                              value={block.body}
                              onChange={(e) => updateLinksBody(block.id, e.target.value)}
                              placeholder={'https://example.com/article\nhttps://youtube.com/watch?v=...'}
                              rows={5}
                            />
                          </label>
                          {block.links && block.links.length > 0 ? (
                            <BlockHelp>{block.links.length} link(s) ready for students.</BlockHelp>
                          ) : null}
                        </BlockFieldGrid>
                      ) : null}

                      {block.type === 'text' ? (
                        <BlockTextarea
                          value={block.body}
                          onChange={(e) => updateBlock(block.id, { body: e.target.value })}
                          placeholder="Content for this block…"
                        />
                      ) : null}
                      {blockError ? <ErrorText>{blockError}</ErrorText> : null}
                    </BlockBody>
                  </BlockCard>
                );
              })}

              <DropZone>Click a block to add content — use the Content Library on the right.</DropZone>
            </>
          ) : null}

          {activeStep === 3 ? (
            <>
              <MainTitle>Assignment</MainTitle>
              <MainSubtitle>Attach existing class assignments so they appear inside this lesson for students.</MainSubtitle>
              <InfoPanel>
                {classAssignments.length === 0 ? (
                  <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>
                    No assignments found for this class. Create one first in{' '}
                    <Link to="/classroom-teacher/assignments">Assignments</Link>.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: '0.55rem' }}>
                    {classAssignments.map((assignment) => {
                      const alreadyLinked = blocks.some(
                        (block) => block.type === 'assignment' && block.assignmentId === assignment.id
                      );
                      return (
                        <div
                          key={assignment.id}
                          style={{
                            border: '1px solid var(--outline)',
                            borderRadius: 10,
                            padding: '0.6rem 0.7rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '0.75rem',
                          }}
                        >
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text)' }}>
                              {assignment.title}
                            </div>
                            <div style={{ fontSize: '0.76rem', color: 'var(--textSecondary)' }}>
                              {assignment.points} pts
                              {assignment.due_at ? ` · Due ${new Date(assignment.due_at).toLocaleString()}` : ' · No due date'}
                            </div>
                          </div>
                          <TinyButton
                            type="button"
                            disabled={alreadyLinked}
                            onClick={() =>
                              setBlocks((prev) => [
                                ...prev,
                                {
                                  ...defaultBlock('assignment'),
                                  title: assignment.title,
                                  meta: `Linked assignment: ${assignment.title}`,
                                  assignmentId: assignment.id,
                                  assignmentTitle: assignment.title,
                                },
                              ])
                            }
                          >
                            {alreadyLinked ? 'Added' : 'Add to lesson'}
                          </TinyButton>
                        </div>
                      );
                    })}
                  </div>
                )}
              </InfoPanel>
            </>
          ) : null}

          {activeStep === 4 ? (
            <>
              <MainTitle>Lesson settings</MainTitle>
              <MainSubtitle>Visibility, scheduling, and student access options will appear here.</MainSubtitle>
              <InfoPanel>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--textSecondary, var(--onSurfaceVariant))' }}>
                  Settings panel coming soon.
                </p>
              </InfoPanel>
            </>
          ) : null}
        </MainColumn>

        <BuilderFooterBar>
          <FooterLeft>
            <FooterLinkBtn type="button" onClick={() => setPreviewOpen(true)}>
              <FaEye size={14} aria-hidden />
              Preview Lesson
            </FooterLinkBtn>
            <FooterLinkBtn type="button" onClick={() => handleSaveDraft()} disabled={saveLesson.isPending}>
              <FaSave size={14} aria-hidden />
              {saveLesson.isPending ? 'Saving…' : 'Save Draft'}
            </FooterLinkBtn>
          </FooterLeft>
          <FooterActions>
            <FooterSecondary
              type="button"
              onClick={() => {
                setTitleError('');
                setActiveStep((s) => (s > 1 ? ((s - 1) as BuilderStep) : s));
              }}
              disabled={saveLesson.isPending || activeStep === 1}
            >
              ← Previous
            </FooterSecondary>
            <FooterPrimary
              type="button"
              onClick={() => {
                if (activeStep < 4) {
                  if (activeStep === 1 && !title.trim()) {
                    setTitleError('Lesson title is required.');
                    return;
                  }
                  setTitleError('');
                  setActiveStep((s) => (s < 4 ? ((s + 1) as BuilderStep) : s));
                  return;
                }
                handlePublishAndLeave();
              }}
              disabled={saveLesson.isPending}
            >
              {activeStep < 4 ? 'Next: ' : 'Finish & '}
              {activeStep === 1
                ? 'Build Content →'
                : activeStep === 2
                  ? 'Add Assignment →'
                  : activeStep === 3
                    ? 'Settings →'
                    : 'Back to lessons'}
            </FooterPrimary>
          </FooterActions>
        </BuilderFooterBar>
        </MiddleColumnWrap>

        <RightColumn>
          <SideLabel>CONTENT LIBRARY</SideLabel>
          <div
            style={{
              border: '1px solid var(--outline)',
              borderRadius: '14px',
              background: 'var(--surface)',
              padding: '0.85rem 0.75rem',
            }}
          >
            <LibraryGrid>
              <LibraryBtn type="button" onClick={() => addBlock('text')}>
                <FaParagraph size={18} aria-hidden />
                Text
              </LibraryBtn>
              <LibraryBtn type="button" onClick={() => addBlock('video')}>
                <FaFilm size={18} aria-hidden />
                Video
              </LibraryBtn>
              <LibraryBtn type="button" onClick={() => addBlock('image')}>
                <FaImage size={18} aria-hidden />
                Image
              </LibraryBtn>
              <LibraryBtn type="button" onClick={() => addBlock('file')}>
                <FaFileAlt size={18} aria-hidden />
                File
              </LibraryBtn>
              <LibraryBtn type="button" onClick={() => addBlock('pdf_pages')}>
                <FaFileAlt size={18} aria-hidden />
                PDF page(s)
              </LibraryBtn>
              <LibraryBtn type="button" onClick={() => addBlock('quiz')}>
                <FaQuestion size={18} aria-hidden />
                Quiz
              </LibraryBtn>
              <LibraryBtn type="button" onClick={() => addBlock('assignment')}>
                <FaClipboardList size={18} aria-hidden />
                Assignment
              </LibraryBtn>
              <LibraryBtn type="button" onClick={() => addBlock('links')}>
                <FaLink size={18} aria-hidden />
                Links
              </LibraryBtn>
            </LibraryGrid>

            <StatusRow>
              <AutoSaveBadge>AUTO-SAVE READY</AutoSaveBadge>
              <LastSaved>
                Last saved: {lastSavedAt ? formatRelativeSaved(lastSavedAt) : 'Not saved yet — use Save Draft'}
              </LastSaved>
            </StatusRow>

            <ProTip>
              <ProTipHead>
                <FaLightbulb size={14} aria-hidden />
                PRO TIP
              </ProTipHead>
              <ProTipBody>
                Combine short videos with a follow-up quiz block to reinforce key ideas and improve retention.
              </ProTipBody>
            </ProTip>
          </div>
        </RightColumn>
      </LayoutGrid>

      {previewOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              borderRadius: '16px',
              maxWidth: '520px',
              width: '100%',
              padding: '1.25rem',
              border: '1px solid var(--outline)',
              boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
            }}
          >
            <h2 style={{ margin: '0 0 0.5rem', fontFamily: "'Poppins', sans-serif", fontSize: '1.15rem' }}>Preview</h2>
            <p style={{ margin: '0 0 0.75rem', fontWeight: 700 }}>{title.trim() || 'Untitled lesson'}</p>
            <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', color: 'var(--textSecondary)' }}>
              {plainDescription || 'No summary yet.'}
            </p>
            <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: 'var(--textSecondary)' }}>
              {blocks.length} content block{blocks.length === 1 ? '' : 's'} in this lesson.
            </p>
            <button
              type="button"
              onClick={() => setPreviewOpen(false)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: 'none',
                background: 'var(--primary)',
                color: 'var(--onPrimary)',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}
    </Shell>
  );
};

export default ClassroomTeacherClassLessonEditorPage;
